import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CreditCard, User, Mail, Phone, Sparkles, Check, Zap, MailCheck, Gift, FileText, Video, Info, MapPin, Globe, LineChart } from "lucide-react";
import { StripeLogo, CashfreeLogo, PaymentProcessorsBadge } from "@/components/icons/PaymentLogos";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useGeoCurrency } from "@/hooks/useGeoCurrency";
import { useAuth } from "@/hooks/use-auth";
import { z } from "zod";
import { IntlPhoneInput } from "@/components/ui/intl-phone-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import { applyGst, formatInr } from "@/lib/gst";
import { INDIAN_STATES } from "@/lib/indianStates";

const indianPhoneSchema = z.object({
  phone: z.string().regex(/^[6-9]\d{9}$/, "Please enter a valid 10-digit Indian mobile number"),
});

interface SubscribeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Raw amounts for Cashfree (single tier)
const PRICES = {
  INR: { monthly: 999, annual: 9999 },
  USD: { monthly: 39, annual: 399 },
};

export const SubscribeModal = ({ isOpen, onClose }: SubscribeModalProps) => {
  const [selectedPlan, setSelectedPlan] = useState<"monthly" | "annual">("monthly");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [stateError, setStateError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [intlInr, setIntlInr] = useState<{ monthly: number | null; annual: number | null }>({
    monthly: null,
    annual: null,
  });
  const { toast } = useToast();
  const { isIndian } = useGeoCurrency();
  const { user } = useAuth();

  useEffect(() => {
    if (!isOpen || isIndian) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("products")
        .select("name, intl_inr_amount")
        .in("name", ["Research Hub Monthly", "Research Hub Annual"])
        .eq("active", true);
      if (cancelled || !data) return;
      const monthly = data.find((d: any) => d.name === "Research Hub Monthly")?.intl_inr_amount ?? null;
      const annual = data.find((d: any) => d.name === "Research Hub Annual")?.intl_inr_amount ?? null;
      setIntlInr({ monthly, annual });
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isIndian]);

  const formatInr = (n: number | null) =>
    n != null ? `₹${n.toLocaleString("en-IN")}` : null;
  const activeIntlInr = selectedPlan === "monthly" ? intlInr.monthly : intlInr.annual;

  const userName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split("@")[0] || "";
  const userEmail = user?.email || "";

  const tierPrices = isIndian ? PRICES.INR : PRICES.USD;
  const symbol = isIndian ? "₹" : "$";
  const activePriceNum = selectedPlan === "monthly" ? tierPrices.monthly : tierPrices.annual;
  const period = selectedPlan === "monthly" ? "/month" : "/year";

  // GST breakdown for INR. International (USD) is zero-rated and skipped entirely.
  const gstBreakdown = isIndian ? applyGst(activePriceNum) : null;
  const displayPrice = isIndian
    ? formatInr(gstBreakdown!.total)
    : `${symbol}${activePriceNum}`;

  const monthlyPrice = isIndian
    ? `${symbol}${tierPrices.monthly.toLocaleString("en-IN")}`
    : `${symbol}${tierPrices.monthly}`;
  const annualPrice = isIndian
    ? `${symbol}${tierPrices.annual.toLocaleString("en-IN")}`
    : `${symbol}${tierPrices.annual}`;

  const monthlyTotal = tierPrices.monthly * 12;
  const annualSavings = monthlyTotal > tierPrices.annual
    ? Math.round(((monthlyTotal - tierPrices.annual) / monthlyTotal) * 100)
    : 0;

  const validateForm = () => {
    if (isIndian) {
      try {
        indianPhoneSchema.parse({ phone });
        setPhoneError("");
        if (!stateCode) {
          setStateError("Please select your state for GST");
          return false;
        }
        setStateError("");
        return true;
      } catch (error) {
        if (error instanceof z.ZodError) {
          setPhoneError(error.errors[0]?.message || "Invalid phone number");
        }
        return false;
      }
    }
    if (!phone || !isValidPhoneNumber(phone)) {
      setPhoneError("Please select your country and enter a valid phone number");
      return false;
    }
    setPhoneError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const returnUrl = `${window.location.origin}/subscription/verify`;

      const { data, error } = await supabase.functions.invoke("create-subscription", {
        body: {
          planType: selectedPlan,
          userName,
          userEmail,
          userPhone: phone,
          returnUrl,
          currency: isIndian ? "INR" : "USD",
          buyerState: isIndian ? INDIAN_STATES.find(s => s.code === stateCode)?.name : undefined,
          buyerStateCode: isIndian ? stateCode : undefined,
        },
      });

      if (error) {
        // Surface the real edge function error message if available
        let detailMsg = error.message;
        try {
          const ctxJson = await (error as any)?.context?.json?.();
          if (ctxJson?.error) detailMsg = ctxJson.error;
        } catch {}
        throw new Error(detailMsg);
      }
      if (!data.success) throw new Error(data.error || "Failed to create subscription");
      if (!data.subscriptionSessionId || !data.subscriptionId) {
        throw new Error("No subscription session received");
      }

      sessionStorage.setItem("subscription-pending", JSON.stringify({
        subscriptionId: data.subscriptionId,
        returnTo: window.location.pathname,
      }));

      // Launch Cashfree-hosted subscription checkout (shows Card / UPI / eNACH per merchant config)
      const { load: loadCashfree } = await import("@cashfreepayments/cashfree-js");
      const cashfree = await loadCashfree({ mode: "production" });
      await cashfree.subscriptionsCheckout({
        subsSessionId: data.subscriptionSessionId,
        returnUrl: `${window.location.origin}/subscription/verify?subscriptionId=${encodeURIComponent(data.subscriptionId)}`,
        redirectTarget: "_self",
      });
    } catch (error) {
      console.error("Subscription error:", error);
      toast({
        title: "Subscription Error",
        description: error instanceof Error ? error.message : "Failed to initiate subscription.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  // Stripe flow (international only) — no phone validation required; Stripe collects card directly.
  const handleStripeSubmit = async () => {
    if (!user) {
      toast({ title: "Please sign in first", variant: "destructive" });
      return;
    }
    setStripeLoading(true);
    try {
      const returnUrl = `${window.location.origin}/subscription/verify`;
      const { data, error } = await supabase.functions.invoke("create-stripe-subscription", {
        body: {
          subscriptionType: selectedPlan === "monthly" ? "club_monthly" : "club_annual",
          userName,
          returnUrl,
        },
      });
      if (error) {
        let msg = error.message;
        try {
          const ctx = await (error as any)?.context?.json?.();
          if (ctx?.error) msg = ctx.error;
        } catch {}
        throw new Error(msg);
      }
      if (!data?.success || !data.checkoutUrl) {
        throw new Error(data?.error || "Could not start Stripe checkout");
      }
      sessionStorage.setItem("subscription-pending", JSON.stringify({
        subscriptionId: data.internalSubId,
        provider: "stripe",
        returnTo: window.location.pathname,
      }));
      window.location.href = data.checkoutUrl;
    } catch (err) {
      toast({
        title: "Stripe Checkout Error",
        description: err instanceof Error ? err.message : "Failed to start Stripe checkout.",
        variant: "destructive",
      });
      setStripeLoading(false);
    }
  };

  const handlePhoneChange = (value: string) => {
    setPhone(value);
    if (phoneError) setPhoneError("");
  };

  if (!isOpen) return null;

  const features = [
    { icon: <FileText className="w-3.5 h-3.5 text-accent" />, text: "3–4 premium AI workflows every month" },
    { icon: <Globe className="w-3.5 h-3.5 text-accent" />, text: "US and global stock research, every Research Dive in full, on the newsletter" },
    { icon: <LineChart className="w-3.5 h-3.5 text-accent" />, text: "The model portfolio, a model book of dated entries, and the valuation model behind each dive" },
    { icon: <Zap className="w-3.5 h-3.5 text-accent" />, text: "Full access to the premium research archive" },
    { icon: <Video className="w-3.5 h-3.5 text-accent" />, text: "Free access to all future live masterclasses" },
    { icon: <MailCheck className="w-3.5 h-3.5 text-accent" />, text: "Priority email support" },
    { icon: <Gift className="w-3.5 h-3.5 text-accent" />, text: "Early-bird & exclusive discount offers" },
  ];

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="w-full max-w-md bg-secondary border border-border rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Subscribe to Premium
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                One simple plan. Everything included.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-muted transition-colors"
              disabled={isLoading}
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Features */}
            <div className="bg-muted/30 rounded-xl p-4 border border-border/50 space-y-2.5">
              <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">What's included</p>
              {features.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                  <span className="mt-0.5 shrink-0">{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </div>

            {/* Plan Selector */}
            <div className="grid grid-cols-2 gap-3">
              {(["monthly", "annual"] as const).map((planId) => {
                const isSelected = selectedPlan === planId;
                const price = planId === "monthly" ? monthlyPrice : annualPrice;
                const periodLabel = planId === "monthly" ? "/month" : "/year";
                return (
                  <button
                    key={planId}
                    type="button"
                    onClick={() => setSelectedPlan(planId)}
                    className={`relative p-4 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                      isSelected
                        ? "border-primary bg-primary/10 shadow-[0_0_20px_hsl(var(--primary)/0.15)]"
                        : "border-border hover:border-muted-foreground/30"
                    }`}
                  >
                    {planId === "annual" && annualSavings > 0 && (
                      <span className="absolute -top-2.5 right-3 px-2 py-0.5 text-[10px] font-bold bg-accent text-accent-foreground rounded-lg">
                        Save {annualSavings}%
                      </span>
                    )}
                    <div className="flex items-center gap-2 mb-1">
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          isSelected
                            ? "border-primary bg-primary"
                            : "border-muted-foreground/40"
                        }`}
                      >
                        {isSelected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                      </div>
                      <span className="text-sm font-medium text-foreground capitalize">{planId}</span>
                    </div>
                    <p className="text-lg font-bold text-foreground font-mono ml-6">
                      {price}
                      <span className="text-xs font-normal text-muted-foreground">{periodLabel}</span>
                    </p>
                    {isIndian && (
                      <p className="text-[10px] text-muted-foreground ml-6 mt-0.5">
                        Exclusive of GST
                      </p>
                    )}
                  </button>
                );
              })}
            </div>

            {!isIndian && (
              <div className="bg-accent/10 border border-accent/30 rounded-xl p-4 flex gap-3">
                <Info className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                <div className="text-xs text-foreground/90 space-y-1">
                  <p className="font-semibold text-foreground">Charged in USD via Stripe or Cashfree</p>
                  <p className="text-muted-foreground">
                    Your card will be charged in USD at the price shown below by your chosen processor
                    (Stripe or Cashfree). International cards (Visa, Mastercard, Amex) are accepted.
                    Your bank may apply its own foreign-exchange conversion to your local currency.
                  </p>
                </div>
              </div>
            )}

            {/* Signed-in user info */}
            <div className="bg-muted/40 rounded-xl p-4 border border-border/50 space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" />
                  Name
                </Label>
                <p className="text-sm font-medium text-foreground">{userName}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  Email
                </Label>
                <p className="text-sm font-medium text-foreground">{userEmail}</p>
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="sub-phone" className="text-foreground flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                Phone Number
              </Label>
              {isIndian ? (
                <Input
                  id="sub-phone"
                  value={phone}
                  onChange={(e) =>
                    handlePhoneChange(e.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                  placeholder="10-digit mobile number"
                  className={phoneError ? "border-destructive" : ""}
                  disabled={isLoading}
                />
              ) : (
                <IntlPhoneInput
                  id="sub-phone"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="Phone number"
                  disabled={isLoading}
                  hasError={!!phoneError}
                />
              )}
              {phoneError && <p className="text-sm text-destructive">{phoneError}</p>}
            </div>

            {/* State (INR only — required for GST place-of-supply) */}
            {isIndian && (
              <div className="space-y-2">
                <Label htmlFor="sub-state" className="text-foreground flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  State (for GST invoice)
                </Label>
                <select
                  id="sub-state"
                  value={stateCode}
                  onChange={(e) => { setStateCode(e.target.value); if (stateError) setStateError(""); }}
                  disabled={isLoading}
                  className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 ${stateError ? "border-destructive" : "border-input"}`}
                >
                  <option value="">Select your state</option>
                  {INDIAN_STATES.map((s) => (
                    <option key={s.code} value={s.code}>{s.name}</option>
                  ))}
                </select>
                {stateError && <p className="text-sm text-destructive">{stateError}</p>}
              </div>
            )}

            {/* Price Display */}
            <div className="bg-muted/50 rounded-xl p-4 border border-border">
              {isIndian && gstBreakdown ? (
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Base price</span>
                    <span className="font-mono text-foreground">{formatInr(gstBreakdown.base)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">GST (18%)</span>
                    <span className="font-mono text-foreground">{formatInr(gstBreakdown.gst)}</span>
                  </div>
                  <div className="h-px bg-border my-1.5" />
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium">Total payable</span>
                    <span className="text-2xl font-bold text-primary font-mono">
                      {formatInr(gstBreakdown.total)}
                      <span className="text-xs font-normal text-muted-foreground">{period}</span>
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Subscription</span>
                  <span className="text-2xl font-bold text-primary font-mono">
                    {displayPrice}
                    <span className="text-xs font-normal text-muted-foreground">{period}</span>
                  </span>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Full amount charged immediately upon authorization. Auto-renews each {selectedPlan === "monthly" ? "month" : "year"}. Cancel anytime.
              </p>
              {!isIndian && (
                <div className="mt-3 flex justify-center">
                  <PaymentProcessorsBadge />
                </div>
              )}
            </div>

            {/* Submit */}
            {!isIndian ? (
              <div className="space-y-3">
                <Button
                  type="button"
                  onClick={handleStripeSubmit}
                  className="w-full h-12 text-base font-semibold gap-2.5 [&_svg]:!h-7 [&_svg]:!w-auto"
                  disabled={isLoading || stripeLoading}
                >
                  {stripeLoading ? (
                    <>
                      <Loader2 className="!h-5 !w-5 mr-2 animate-spin" />
                      Redirecting to Stripe...
                    </>
                  ) : (
                    <>
                      <span className="text-[15px]">Pay with</span>
                      <StripeLogo className="h-7 w-auto" />
                    </>
                  )}
                </Button>
                <Button
                  type="submit"
                  variant="outline"
                  className="w-full h-12 text-base font-semibold gap-2.5 [&_svg]:!h-7 [&_svg]:!w-auto"
                  disabled={isLoading || stripeLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="!h-5 !w-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <span className="text-[15px]">Pay with</span>
                      <CashfreeLogo className="h-7 w-auto" />
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Choose whichever processor your card works with. Stripe supports most international cards plus Apple Pay and Google Pay; Cashfree is an alternate processor.
                </p>
              </div>
            ) : (
              <>
                <Button type="submit" className="w-full h-12 text-lg font-mono font-bold" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Subscribe Now
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Secured by Cashfree. Cancel anytime.
                </p>
              </>
            )}
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};
